import { useEffect, useState } from "react";
import { XIcon } from "lucide-react";

import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { Row } from "@/components/Row";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { useDeleteView } from "@/hooks/mutations/useDeleteView";
import { useUpdateView } from "@/hooks/mutations/useUpdateView";
import { useRemoveView } from "@/hooks/sets/useRemoveView";
import { useSetView } from "@/hooks/sets/useSetView";
import { cn } from "@/lib/utils";
import { ViewQueryParams } from "@/types";

interface TableViewButtonView {
  id: number | string;
  isDefault?: boolean;
  name: string;
}

export function TableViewButton<T extends TableViewButtonView>({
  handleSelectView,
  selectedView,
  setSelectedView,
  view,
  viewCounts,
  viewQueryParams,
  views,
}: {
  handleSelectView: (view: T) => void;
  selectedView?: T;
  setSelectedView: (view: T) => void;
  view: T;
  viewCounts?: Record<string, number>;
  viewQueryParams: ViewQueryParams;
  views: T[];
}) {
  /* React Hooks */
  /* useStates */
  const [editingName, setEditingName] = useState(view.name);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // React Hooks
  useEffect(() => {
    if (!isEditing) {
      setEditingName(view.name);
    }
  }, [isEditing, view.name]);

  /* Database Queries and Cache Updates */
  // Updates
  const { mutate: updateView } = useUpdateView();
  const setView = useSetView();

  // Deletes
  const { mutate: deleteView } = useDeleteView();
  const removeView = useRemoveView();

  /* Computed Values */
  const viewKey = String(view.id);
  const isSelected = String(selectedView?.id) === viewKey;
  const canEdit = view.isDefault !== true;
  const canDelete = view.isDefault !== true && views.length > 1;
  const count = viewCounts?.[viewKey];

  /* Handlers */
  const handleDeleteConfirm = () => {
    if (view.isDefault || !canDelete) {
      setIsDeleteDialogOpen(false);
      return;
    }

    const id = view.id as number;
    const isDeletingSelectedView = selectedView?.id === view.id;
    deleteView(
      { id },
      {
        onSuccess: () => {
          removeView({ id, viewQueryParams });
          if (isDeletingSelectedView) {
            const nextView = views.find(
          (v) => v.id !== id && v.id !== "default-sourced",
        );
            if (nextView) {
              setSelectedView(nextView);
            }
          }
        },
      },
    );
    setIsDeleteDialogOpen(false);
  };

  const handleUpdateViewName = (newName: string) => {
    if (view.isDefault) return;
    updateView(
      { id: view.id as number, updateViewRequest: { name: newName } },
      {
        onSuccess: (updatedView) => {
          setView({ updatedView, viewQueryParams });
        },
      },
    );
  };

  if (canEdit && isEditing) {
    return (
      <Input
        key={viewKey}
        autoFocus
        className="h-8 w-auto"
        onBlur={(e) => {
          const name = e.target.value.trim();
          if (name === "" || name === view.name) {
            setEditingName(view.name);
            setIsEditing(false);
            return;
          }
          handleUpdateViewName(name);
          setIsEditing(false);
        }}
        onChange={(e) => {
          setEditingName(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur();
          } else if (e.key === "Escape") {
            setEditingName(view.name);
            setIsEditing(false);
          }
        }}
        value={editingName}
      />
    );
  }

  return (
    <Row key={viewKey} className="group relative">
      <Button
        onClick={() => {
          handleSelectView(view);
        }}
        onDoubleClick={() => {
          if (canEdit) {
            setEditingName(view.name);
            setIsEditing(true);
          }
        }}
        variant="outlineSecondary"
        className={cn(
          "h-8 gap-2 border-0 group-hover:bg-primary-200",
          canDelete && "group-hover:pr-10",
          isSelected && "border border-primary-700",
        )}
      >
        {view.name}
        {count !== undefined && (
          <Badge className="text-xs" variant="secondary">
            {count}
          </Badge>
        )}
      </Button>
      {canDelete && (
        <Button
          className="absolute right-0 top-2 hidden h-3 w-3 text-slate-400 hover:bg-transparent hover:text-slate-900 group-hover:flex"
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteDialogOpen(true);
          }}
          variant="ghost"
        >
          <XIcon className="h-3.5 w-3.5" />
        </Button>
      )}
      <ConfirmationDialog
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description="This action cannot be undone. This will permanently delete the view."
        onConfirm={handleDeleteConfirm}
        onOpenChange={setIsDeleteDialogOpen}
        open={isDeleteDialogOpen}
        title={`Delete view "${view.name}"?`}
      />
    </Row>
  );
}
